import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import useLanguage from 'features/languageReport/useLanguage'
import {Lobe, Roi} from 'api/analysisApi'
import {getPublicFile} from 'helpers/envHelper'
import {ResEEG} from 'api/languageApi'
import useStyle from 'components/molcules/Report/RoiComponent/RoiComponentStyle'
import useLanguageReport from 'hooks/useLanguageReport'

const getEegReportImage = getPublicFile('images/eeg_report')

type Score = [number | string, number | string]
type LobeTableIcon = {
  short: string
  name: string
  image?: string
}
type LobeTableItem = {
  icons: LobeTableIcon[]
  feature: string
  example: string
  symptom: string
  scores: Score[]
  functionLabel?: string
  symptomsLabel?: string
}
type LobeData = {
  name: string
  feature: string
  desc: string
  image: string
  score: Score
  lobeItems: LobeTableItem[]
}

const getFrontalLobeComponentProps = (
  roi: Roi,
  lobe: Lobe,
  textEegAPI: ResEEG | null,
): LobeData => {
  const frontalSection = textEegAPI?.brain_aging.advanced.section.frontal
  return {
    name: frontalSection?.title || '',
    feature:
      [
        frontalSection?.description[0],
        frontalSection?.description[1],
        frontalSection?.description[2],
      ].join('<br />') || '',
    desc:
      [
        frontalSection?.description[3],
        frontalSection?.description[4],
        frontalSection?.description[5],
      ].join(' ') || '',

    image: getEegReportImage('frontal_lobe.jpeg'),
    score: [lobe.Frontal_LH, lobe.Frontal_RH],
    lobeItems: [
      {
        icons: [
          {
            short: 'F1',
            name: frontalSection?.score_table.rows[0].rois[0].name || '',
            image: getEegReportImage('f1.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[0].functions.sub[0]} \n ${frontalSection?.score_table.rows[0].functions.sub[1]} \n ${frontalSection?.score_table.rows[0].functions.sub[2]}` ||
          '',
        example: frontalSection?.score_table.rows[0].functions.sub[1] || '',
        symptom:
          `${frontalSection?.score_table.rows[0].symptoms.sub[0]} \n ${frontalSection?.score_table.rows[0].symptoms.sub[1]}` ||
          '',
        scores: [[roi.Frontal_Pole_LH, roi.Frontal_Pole_RH]],
      },
      {
        icons: [
          {
            short: 'F2',
            name: frontalSection?.score_table.rows[1].rois[0].name || '',
          },
          {
            short: 'F3',
            name: frontalSection?.score_table.rows[1].rois[1].name || '',
          },
          {
            short: 'F4',
            name: frontalSection?.score_table.rows[1].rois[2].name || '',
            image: getEegReportImage('f2_f4.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[1].functions.sub[0]} \n ${frontalSection?.score_table.rows[1].functions.sub[1]} \n ${frontalSection?.score_table.rows[1].functions.sub[2]}` ||
          '',
        example: 'IRoiF2F4ExampleAPI',
        symptom:
          `${frontalSection?.score_table.rows[1].symptoms.sub[0]} \n ${frontalSection?.score_table.rows[1].symptoms.sub[1]}` ||
          '',
        scores: [
          [roi.Pars_Opercularis_LH, roi.Pars_Opercularis_RH],
          [roi.Pars_Orbitalis_LH, roi.Pars_Orbitalis_RH],
          [roi.Pars_Triangularis_LH, roi.Pars_Triangularis_RH],
        ],
      },
      {
        icons: [
          {
            short: 'F5',
            name: frontalSection?.score_table.rows[2].rois[0].name || '',
            image: getEegReportImage('f5.png'),
          },
          {
            short: 'F6',
            name: frontalSection?.score_table.rows[2].rois[1].name || '',
            image: getEegReportImage('f6.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[2].functions.sub[0]} \n ${frontalSection?.score_table.rows[2].functions.sub[1]} \n ${frontalSection?.score_table.rows[2].functions.sub[2]}` ||
          '',
        example: 'IRoiF5F6ExampleAPI',
        symptom:
          `${frontalSection?.score_table.rows[2].symptoms.sub[0]} \n ${frontalSection?.score_table.rows[2].symptoms.sub[1]}` ||
          '',
        scores: [
          [roi.Rostral_Middle_Frontal_LH, roi.Rostral_Middle_Frontal_RH],
          [roi.Caudal_Middle_Frontal_LH, roi.Caudal_Middle_Frontal_RH],
        ],
      },
      {
        icons: [
          {
            short: 'F7',
            name: frontalSection?.score_table.rows[3].rois[0].name || '',
            image: getEegReportImage('f7.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[3].functions.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[3].functions.sub[1] || ''
          } \n ${frontalSection?.score_table.rows[3].functions.sub[2] || ''} \n 
          ${frontalSection?.score_table.rows[3].functions.sub[3]}` || '',
        example: 'IRoiF7ExampleAPI',
        symptom:
          `${frontalSection?.score_table.rows[3].symptoms.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[3].symptoms.sub[1] || ''
          } \n ${frontalSection?.score_table.rows[3].symptoms.sub[2] || ''}` ||
          '',
        scores: [[roi.Superior_Frontal_LH, roi.Superior_Frontal_RH]],
      },
      {
        icons: [
          {
            short: 'F8',
            name: frontalSection?.score_table.rows[4].rois[0].name || '',
            image: getEegReportImage('f8.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[4].functions.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[4].functions.sub[1] || ''
          } \n ${frontalSection?.score_table.rows[4].functions.sub[2] || ''}` ||
          '',
        example: 'IRoiF8ExampleAPI',
        symptom:
          `${frontalSection?.score_table.rows[4].symptoms.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[4].symptoms.sub[1] || ''
          }` || '',
        scores: [[roi.Medial_Orbitofrontal_LH, roi.Medial_Orbitofrontal_RH]],
      },
      {
        icons: [
          {
            short: 'F9',
            name: frontalSection?.score_table.rows[5].rois[0].name || '',
            image: getEegReportImage('f9.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[5].functions.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[5].functions.sub[1] || ''
          } \n ${frontalSection?.score_table.rows[5].functions.sub[2] || ''}` ||
          '',
        example: 'IRoiF9ExampleAPI',
        symptom:
          `${frontalSection?.score_table.rows[5].symptoms.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[5].symptoms.sub[1] || ''
          }` || '',
        scores: [[roi.Lateral_Orbitofrontal_LH, roi.Lateral_Orbitofrontal_RH]],
      },
      {
        icons: [
          {
            short: 'F10',
            name: frontalSection?.score_table.rows[6].rois[0].name || '',
            image: getEegReportImage('f10.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[6].functions.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[6].functions.sub[1] || ''
          } \n ${frontalSection?.score_table.rows[6].functions.sub[2] || ''}` ||
          '',
        example: 'IRoiF10ExampleAPI',
        symptom:
          `${frontalSection?.score_table.rows[6].symptoms.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[6].symptoms.sub[1] || ''
          }` || '',
        scores: [[roi.Precentral_LH, roi.Precentral_RH]],
      },
      {
        icons: [
          {
            short: 'F11',
            name: frontalSection?.score_table.rows[7].rois[0].name || '',
            image: getEegReportImage('f11.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[7].functions.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[7].functions.sub[1] || ''
          } \n ${frontalSection?.score_table.rows[7].functions.sub[2] || ''}` ||
          '',
        example: 'IRoiF11ExampleAPI',
        symptom:
          `${frontalSection?.score_table.rows[7].symptoms.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[7].symptoms.sub[1] || ''
          }` || '',
        scores: [[roi.Paracentral_LH, roi.Paracentral_RH]],
      },
      {
        icons: [
          {
            short: 'F12',
            name: frontalSection?.score_table.rows[8].rois[0].name || '',
            image: getEegReportImage('f12.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[8].functions.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[8].functions.sub[1] || ''
          } \n ${frontalSection?.score_table.rows[8].functions.sub[2] || ''}` ||
          '',
        example: 'IRoiF12ExampleAPI',
        symptom:
          `${frontalSection?.score_table.rows[8].symptoms.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[8].symptoms.sub[1] || ''
          }` || '',
        scores: [
          [
            roi.Rostral_Anterior_Cingulate_LH,
            roi.Rostral_Anterior_Cingulate_RH,
          ],
        ],
      },
      {
        icons: [
          {
            short: 'F13',
            name: frontalSection?.score_table.rows[9].rois[0].name || '',
            image: getEegReportImage('f13.png'),
          },
        ],
        feature:
          `${frontalSection?.score_table.rows[9].functions.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[9].functions.sub[1] || ''
          } \n ${frontalSection?.score_table.rows[9].functions.sub[2] || ''}` ||
          '',
        example: 'IRoiF13ExampleAPI',
        symptom:
          `${frontalSection?.score_table.rows[9].symptoms.sub[0] || ''} \n ${
            frontalSection?.score_table.rows[9].symptoms.sub[1] || ''
          }` || '',
        scores: [
          [roi.Caudal_Anterior_Cingulate_LH, roi.Caudal_Anterior_Cingulate_RH],
        ],
      },
    ],
  }
}

const getTemporalLobeComponentProps = (
  roi: Roi,
  lobe: Lobe,
  textEegAPI: ResEEG | null,
): LobeData => {
  const temporalSection = textEegAPI?.brain_aging.advanced.section.temporal
  return {
    name: temporalSection?.title || '',
    feature: `${
      [
        temporalSection?.description[0],
        temporalSection?.description[1],
        temporalSection?.description[2],
      ].join('<br />') || ''
    }`,
    desc: `${
      [
        temporalSection?.description[3],
        temporalSection?.description[4],
        temporalSection?.description[5],
      ].join(' ') || ''
    }`,
    image: getEegReportImage('temporal_lobe.jpeg'),
    score: [lobe.Temporal_LH, lobe.Temporal_RH],
    lobeItems: [
      {
        icons: [
          {
            short: 'T1',
            name: temporalSection?.score_table.rows[0].rois[0].name || '',
            image: getEegReportImage('t1.png'),
          },
        ],
        feature:
          `${temporalSection?.score_table.rows[0].functions.sub[0] || ''} \n ${
            temporalSection?.score_table.rows[0].functions.sub[1] || ''
          } \n ${
            temporalSection?.score_table.rows[0].functions.sub[2] || ''
          }` || '',
        example: 'IRoiT1ExampleAPI',
        symptom:
          `${temporalSection?.score_table.rows[0].symptoms.sub[0] || ''} \n ${
            temporalSection?.score_table.rows[0].symptoms.sub[1] || ''
          } \n ${
            temporalSection?.score_table.rows[0].functions.sub[2] || ''
          }` || '',
        scores: [[roi.Temporal_Pole_LH, roi.Temporal_Pole_RH]],
      },
      {
        icons: [
          {
            short: 'T2',
            name: temporalSection?.score_table.rows[1].rois[0].name || '',
            image: getEegReportImage('t2.png'),
          },
        ],
        feature:
          `${temporalSection?.score_table.rows[1].functions.sub[0] || ''} \n ${
            temporalSection?.score_table.rows[1].functions.sub[1] || ''
          } \n ${
            temporalSection?.score_table.rows[1].functions.sub[2] || ''
          }` || '',
        example: 'IRoiT2ExampleAPI',
        symptom:
          `${temporalSection?.score_table.rows[1].symptoms.sub[0] || ''} \n ${
            temporalSection?.score_table.rows[1].symptoms.sub[1] || ''
          } \n ${temporalSection?.score_table.rows[1].symptoms.sub[2]}` || '',
        scores: [[roi.Superior_Temporal_LH, roi.Superior_Temporal_RH]],
      },
      {
        icons: [
          {
            short: 'T3',
            name: temporalSection?.score_table.rows[2].rois[0].name || '',
            image: getEegReportImage('t3.png'),
          },
        ],
        feature: `${
          temporalSection?.score_table.rows[2].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiT3ExampleAPI',
        symptom: `${
          temporalSection?.score_table.rows[2].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Middle_Temporal_LH, roi.Middle_Temporal_RH]],
      },
      {
        icons: [
          {
            short: 'T4',
            name: temporalSection?.score_table.rows[3].rois[0].name || '',
            image: getEegReportImage('t4.png'),
          },
        ],
        feature: `${
          temporalSection?.score_table.rows[3].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiT4ExampleAPI',
        symptom: `${
          temporalSection?.score_table.rows[3].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Inferior_Temporal_LH, roi.Inferior_Temporal_RH]],
      },
      {
        icons: [
          {
            short: 'T5',
            name: temporalSection?.score_table.rows[4].rois[0].name || '',
            image: getEegReportImage('t5.png'),
          },
        ],
        feature: `${
          temporalSection?.score_table.rows[4].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiT5ExampleAPI',
        symptom: `${
          temporalSection?.score_table.rows[4].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Transverse_Temporal_LH, roi.Transverse_Temporal_RH]],
      },
      {
        icons: [
          {
            short: 'T6',
            name: temporalSection?.score_table.rows[5].rois[0].name || '',
            image: getEegReportImage('t6.png'),
          },
        ],
        feature: `${
          temporalSection?.score_table.rows[5].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiT6ExampleAPI',
        symptom: `${
          temporalSection?.score_table.rows[5].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [
          [
            roi.Banks_of_Superior_Temporal_Sulcus_LH,
            roi.Banks_of_Superior_Temporal_Sulcus_RH,
          ],
        ],
      },
      {
        icons: [
          {
            short: 'T7',
            name: temporalSection?.score_table.rows[6].rois[0].name ?? '',
            image: getEegReportImage('t7.png'),
          },
        ],
        feature: `${
          temporalSection?.score_table.rows[6].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiT7ExampleAPI',
        symptom: `${
          temporalSection?.score_table.rows[6].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Fusiform_LH, roi.Fusiform_RH]],
      },
      {
        icons: [
          {
            short: 'T8',
            name: temporalSection?.score_table.rows[7].rois[0].name || '',
            image: getEegReportImage('t8.png'),
          },
        ],
        feature: `${
          temporalSection?.score_table.rows[7].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiT8ExampleAPI',
        symptom: `${
          temporalSection?.score_table.rows[7].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Entorhinal_LH, roi.Entorhinal_RH]],
      },
      {
        icons: [
          {
            short: 'T9',
            name: temporalSection?.score_table.rows[8].rois[0].name ?? '',
            image: getEegReportImage('t9.png'),
          },
        ],
        feature: `${
          temporalSection?.score_table.rows[8].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiT9ExampleAPI',
        symptom: `${
          temporalSection?.score_table.rows[8].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Parahippocampal_LH, roi.Parahippocampal_RH]],
      },
      {
        icons: [
          {
            short: 'T10',
            name: temporalSection?.score_table.rows[9].rois[0].name ?? '',
            image: getEegReportImage('t10.png'),
          },
        ],
        feature: `${
          temporalSection?.score_table.rows[9].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiT10ExampleAPI',
        symptom: `${
          temporalSection?.score_table.rows[9].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Insula_LH, roi.Insula_RH]],
      },
    ],
  }
}

const getParietalLobeComponentProps = (
  roi: Roi,
  lobe: Lobe,
  textEegAPI: ResEEG | null,
): LobeData => {
  const parietalSection = textEegAPI?.brain_aging.advanced.section.parietal
  return {
    name: parietalSection?.title || '',
    feature: `${
      [
        parietalSection?.description[0],
        parietalSection?.description[1],
        parietalSection?.description[2],
      ].join('<br />') || ''
    }`,
    desc: `${
      [
        parietalSection?.description[3],
        parietalSection?.description[4],
        parietalSection?.description[5],
      ].join(' ') || ''
    }`,
    image: getEegReportImage('parietal_lobe.jpeg'),
    score: [lobe.Parietal_LH, lobe.Parietal_RH],
    lobeItems: [
      {
        icons: [
          {
            short: 'P1',
            name: parietalSection?.score_table.rows[0].rois[0].name || '',
            image: getEegReportImage('p1.png'),
          },
        ],
        feature: `${
          parietalSection?.score_table.rows[0].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiP1ExampleAPI',
        symptom: `${
          parietalSection?.score_table.rows[0].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Postcentral_LH, roi.Postcentral_RH]],
      },
      {
        icons: [
          {
            short: 'P2',
            name: parietalSection?.score_table.rows[1].rois[0].name || '',
            image: getEegReportImage('p2.png'),
          },
        ],
        feature: `${
          parietalSection?.score_table.rows[1].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiP2ExampleAPI',
        symptom: `${
          parietalSection?.score_table.rows[1].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Superior_Parietal_LH, roi.Superior_Parietal_RH]],
      },
      {
        icons: [
          {
            short: 'P3',
            name: parietalSection?.score_table.rows[2].rois[0].name || '',
            image: getEegReportImage('p3.png'),
          },
        ],
        feature: `${
          parietalSection?.score_table.rows[2].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiP3ExampleAPI',
        symptom: `${
          parietalSection?.score_table.rows[2].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Inferior_Parietal_LH, roi.Inferior_Parietal_RH]],
      },
      {
        icons: [
          {
            short: 'P4',
            name: parietalSection?.score_table.rows[3].rois[0].name || '',
            image: getEegReportImage('p4.png'),
          },
        ],
        feature: `${
          parietalSection?.score_table.rows[3].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiP4ExampleAPI',
        symptom: `${
          parietalSection?.score_table.rows[3].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Supramarginal_LH, roi.Supramarginal_RH]],
      },
      {
        icons: [
          {
            short: 'P5',
            name: parietalSection?.score_table.rows[4].rois[0].name || '',
            image: getEegReportImage('p5.png'),
          },
        ],
        feature: `${
          parietalSection?.score_table.rows[4].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiP5ExampleAPI',
        symptom: `${
          parietalSection?.score_table.rows[4].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Precuneus_LH, roi.Precuneus_RH]],
      },
      {
        icons: [
          {
            short: 'P6',
            name: parietalSection?.score_table.rows[5].rois[0].name || '',
            image: getEegReportImage('p6.png'),
          },
        ],
        feature: `${
          parietalSection?.score_table.rows[5].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiP6ExampleAPI',
        symptom: `${
          parietalSection?.score_table.rows[5].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Posterior_Cingulate_LH, roi.Posterior_Cingulate_RH]],
      },
    ],
  }
}

const getOccipitalLobeComponentProps = (
  roi: Roi,
  lobe: Lobe,
  textEegAPI: ResEEG | null,
): LobeData => {
  const occipitalSection = textEegAPI?.brain_aging.advanced.section.occipital
  return {
    name: occipitalSection?.title || '',
    feature:
      [
        occipitalSection?.description[0],
        occipitalSection?.description[1],
        occipitalSection?.description[2],
      ].join('<br />') || '',
    desc: `${
      [
        occipitalSection?.description[3],
        occipitalSection?.description[4],
        occipitalSection?.description[5],
      ].join(' ') || ''
    }`,
    image: getEegReportImage('occipital_lobe.jpeg'),
    score: [lobe.Occipital_LH, lobe.Occipital_RH],
    lobeItems: [
      {
        icons: [
          {
            short: 'O1',
            name: occipitalSection?.score_table.rows[0].rois[0].name || '',
            image: getEegReportImage('o1.png'),
          },
        ],
        feature: `${
          occipitalSection?.score_table.rows[0].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiO1ExampleAPI',
        symptom: `${
          occipitalSection?.score_table.rows[0].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Lateral_Occipital_LH, roi.Lateral_Occipital_RH]],
      },
      {
        icons: [
          {
            short: 'O2',
            name: occipitalSection?.score_table.rows[1].rois[0].name || '',
            image: getEegReportImage('o2.png'),
          },
        ],
        feature: `${
          occipitalSection?.score_table.rows[1].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiO2ExampleAPI',
        symptom: `${
          occipitalSection?.score_table.rows[1].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Cuneus_LH, roi.Cuneus_RH]],
      },
      {
        icons: [
          {
            short: 'O3',
            name: occipitalSection?.score_table.rows[2].rois[0].name ?? '',
            image: getEegReportImage('o3.png'),
          },
        ],
        feature: `${
          occipitalSection?.score_table.rows[2].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiO3ExampleAPI',
        symptom: `${
          occipitalSection?.score_table.rows[2].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Pericalcarine_LH, roi.Pericalcarine_RH]],
      },
      {
        icons: [
          {
            short: 'O4',
            name: occipitalSection?.score_table.rows[3].rois[0].name || '',
            image: getEegReportImage('o4.png'),
          },
        ],
        feature: `${
          occipitalSection?.score_table.rows[3].functions.sub.join(' \n ') || ''
        }`,
        example: 'IRoiO4ExampleAPI',
        symptom: `${
          occipitalSection?.score_table.rows[3].symptoms.sub.join(' \n ') || ''
        }`,
        scores: [[roi.Lingual_LH, roi.Lingual_RH]],
      },
    ],
  }
}

function RoiTableFeature(props: {
  feature: string
  symptom: string
  example: string
  functionLabel: string
  symptomsLabel: string
}) {
  const {feature, symptom, example, functionLabel, symptomsLabel} = props

  return (
    <td className='Feature'>
      <div className='Wrapper'>
        <div className='Title'>{functionLabel}</div>
        <div className='Text'>{feature}</div>
      </div>
      {/* Hide to same pdf */}
      {/* <div className='Wrapper'>
        <div className='Title'>IExample')}</div>
        <div className='Text'>{t(example)}</div>
      </div> */}
      <div className='Wrapper'>
        <div className='Title'>{symptomsLabel}</div>
        <div className='Text'>{symptom}</div>
      </div>
    </td>
  )
}

function RoiTableIcon({items}: {items: LobeTableIcon[]}) {
  const {t} = useTranslation()
  return (
    <td className='IconList'>
      {items.map((d) => (
        <div className='Icon' key={d.short}>
          <div className='Label'>
            <span className='Short'>{d.short}</span>
            <span className='Name'>{t(d.name)}</span>
          </div>
          {d.image && <img src={d.image} alt='' />}
        </div>
      ))}
    </td>
  )
}

function RoiTableScore({scores}: {scores: Score[]}) {
  const left = scores.map((s) => s[0])
  const right = scores.map((s) => s[1])
  return (
    <React.Fragment>
      <td className='Score AlignMiddle'>
        {left.map((l) => (
          <div key={l}>{l}%ile</div>
        ))}
      </td>
      <td className='Score AlignMiddle'>
        {right.map((r) => (
          <div key={r}>{r}%ile</div>
        ))}
      </td>
    </React.Fragment>
  )
}

function RoiTableItem({
  icons,
  feature,
  example,
  symptom,
  scores,
  functionLabel,
  symptomsLabel,
}: LobeTableItem) {
  return (
    <tr>
      <RoiTableIcon items={icons} />
      <RoiTableFeature
        feature={feature}
        symptom={symptom}
        example={example}
        functionLabel={functionLabel || ''}
        symptomsLabel={symptomsLabel || ''}
      />
      <RoiTableScore scores={scores} />
    </tr>
  )
}

interface RoiTableProps {
  className: string
  name: string
  items: LobeTableItem[]
  functionLabel: string
  symptomsLabel: string
}

function RoiTable(props: RoiTableProps) {
  const {t} = useTranslation()
  const {textEegAPI} = useLanguage('eeg')
  const {name, items, className, functionLabel, symptomsLabel} = props
  return (
    <div className={className}>
      <table>
        <thead>
          <tr>
            <th>{t(name)}</th>
            <th>
              {
                textEegAPI?.brain_aging.advanced.section.frontal.score_table
                  .headers[1]
              }
            </th>
            <th>
              {
                textEegAPI?.brain_aging.advanced.section.frontal.score_table
                  .headers[2]
              }
            </th>
            <th>
              {
                textEegAPI?.brain_aging.advanced.section.frontal.score_table
                  .headers[3]
              }
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <RoiTableItem
              key={idx}
              {...item}
              functionLabel={functionLabel}
              symptomsLabel={symptomsLabel}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface RoiComponentAPIProps {
  roi: Roi
  lobe: Lobe
}

function RoiComponentAPI({roi, lobe}: RoiComponentAPIProps) {
  const {t} = useTranslation()
  const classes = useStyle()
  const {textEegAPI} = useLanguage('eeg')
  const [data, setData] = useState<LobeData[]>([])

  useEffect(() => {
    setData([
      getFrontalLobeComponentProps(roi, lobe, textEegAPI),
      getTemporalLobeComponentProps(roi, lobe, textEegAPI),
      getParietalLobeComponentProps(roi, lobe, textEegAPI),
      getOccipitalLobeComponentProps(roi, lobe, textEegAPI),
    ])
  }, [roi, lobe, textEegAPI])
  const dataTitle = useMemo(() => {
    return [
      textEegAPI?.brain_aging.advanced.section.frontal.score_table.headers[0],
      textEegAPI?.brain_aging.advanced.section.temporal.score_table.headers[0],
      textEegAPI?.brain_aging.advanced.section.parietal.score_table.headers[0],
      textEegAPI?.brain_aging.advanced.section.occipital.score_table.headers[0],
    ]
  }, [roi, lobe, textEegAPI])

  return (
    <div className={classes.root}>
      {data?.map((d, index: number) => (
        <div key={index} className={classes.Lobe}>
          <div className='Title'>{d.name}</div>
          <div
            className='Short'
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{__html: t(d.feature)}}
          />
          <div className='DescWithScore'>
            <div className='Desc'>{t(d.desc)}</div>
            <div className='Score'>
              <div className='Values'>
                <div className='CircleLabel'>
                  {
                    textEegAPI?.brain_aging.advanced.section.frontal
                      .score_by_side.left
                  }
                </div>
                <div>{`${d.score[0]}%ile`}</div>
                <div className='CircleLabel'>
                  {
                    textEegAPI?.brain_aging.advanced.section.frontal
                      .score_by_side.right
                  }
                </div>
                <div>{`${d.score[1]}%ile`}</div>
              </div>
              <div className='Image'>
                <img src={d.image} alt='' />
              </div>
            </div>
          </div>
          <RoiTable
            className={classes.LobeTable}
            name={dataTitle[index] as string}
            items={d.lobeItems}
            functionLabel={
              (textEegAPI?.brain_aging.advanced.section.frontal.score_table
                .rows[0].functions.main as string) || ''
            }
            symptomsLabel={
              (textEegAPI?.brain_aging.advanced.section.frontal.score_table
                .rows[0].symptoms.main as string) || ''
            }
          />
        </div>
      ))}
    </div>
  )
}

export default RoiComponentAPI
